import React, { useState } from 'react';
import { format } from 'date-fns';
import { Disclosure, Transition } from '@headlessui/react';
import { GiCheckMark } from 'react-icons/gi';
import {
     AGREEMENT_VERSION,
     RENTAL_AGREEMENT_SECTIONS,
} from '../../content/rentalAgreement';

export default function BookingReview({ equipmentData, deliveryData, bookingData, contactData, onConfirm, onBack }) {
     const [submitting, setSubmitting] = useState(false);
     const [agreedToSafety, setAgreedToSafety] = useState(false);
     const [agreedToTerms, setAgreedToTerms] = useState(false);
     const [showValidation, setShowValidation] = useState(false);

     const allAgreed = agreedToSafety && agreedToTerms;

     async function handleCheckout() {
          if (!allAgreed) {
               setShowValidation(true);
               return;
          }
          setSubmitting(true);
          try {
               await onConfirm({
                    agreedToSafety,
                    agreedToRentalTerms: agreedToTerms,
                    agreementVersion: AGREEMENT_VERSION,
                    agreementAcceptedAt: new Date().toISOString(),
               });
          } catch (error) {
               setSubmitting(false);
          }
     }

     return (
          <div className='w-full max-w-2xl mx-auto'>
               <h3 className='text-dark uppercase font-bold text-[32px] laptop:text-[40px] leading-tight mb-2'>
                    Review Your Booking
               </h3>
               <p className='text-darker/70 mb-8'>
                    Please review the details below. We'll receive your booking request by email and contact you to confirm availability and payment.
               </p>

               <div className='bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-6'>
                    {/* Equipment */}
                    {equipmentData && (
                         <div>
                              <h4 className='text-yellow text-xs uppercase tracking-widest font-bold mb-3'>
                                   {equipmentData.mode === 'package' ? 'Package' : 'Selected Equipment'}
                              </h4>
                              <ul className='space-y-2'>
                                   {equipmentData.items.map((item) => (
                                        <li key={item.id} className='flex items-start justify-between gap-4 text-darker'>
                                             <span className='flex items-start gap-2 flex-1 min-w-0'>
                                                  <GiCheckMark className='text-yellow text-sm flex-shrink-0 mt-1' />
                                                  <span>
                                                       {item.name}
                                                       {item.selectedQty > 1 && (
                                                            <span className='text-darker/50 text-sm ml-1'>x{item.selectedQty}</span>
                                                       )}
                                                  </span>
                                             </span>
                                             <span className='text-darker/60 text-sm whitespace-nowrap flex-shrink-0'>
                                                  ${((item.daily_rate * (item.selectedQty || 1)) / 100).toLocaleString()}/day
                                             </span>
                                        </li>
                                   ))}
                              </ul>
                              <div className='mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm'>
                                   <span className='text-darker/60'>Daily total</span>
                                   <span className='text-yellow font-bold'>
                                        ${(equipmentData.dailyRateCents / 100).toLocaleString()}/day
                                   </span>
                              </div>
                         </div>
                    )}

                    {/* Delivery */}
                    {deliveryData && (
                         <div className='border-t border-gray-200 pt-4'>
                              <h4 className='text-yellow text-xs uppercase tracking-widest font-bold mb-3'>
                                   Delivery Method
                              </h4>
                              <div className='flex justify-between text-darker'>
                                   <span>
                                        {deliveryData.method === 'delivery' ? 'Delivery to job site' : 'Self pickup'}
                                   </span>
                                   <span className={deliveryData.deliveryFeeCents > 0 ? 'text-yellow font-bold' : 'text-darker/50'}>
                                        {deliveryData.deliveryFeeCents > 0
                                             ? `+$${(deliveryData.deliveryFeeCents / 100).toLocaleString()}`
                                             : 'Free'}
                                   </span>
                              </div>
                              {deliveryData.method === 'delivery' && contactData.address && (
                                   <p className='text-darker/60 text-sm mt-2'>
                                        {contactData.address}, {contactData.city}, {contactData.state} {contactData.zip}
                                   </p>
                              )}
                         </div>
                    )}

                    {/* Dates */}
                    <div className='border-t border-gray-200 pt-4'>
                         <h4 className='text-yellow text-xs uppercase tracking-widest font-bold mb-3'>
                              Rental Period
                         </h4>
                         <div className='grid grid-cols-3 gap-4 text-darker'>
                              <div>
                                   <p className='text-darker/50 text-xs uppercase tracking-wider'>Start</p>
                                   <p className='text-lg font-semibold'>
                                        {format(bookingData.startDate, 'MMM d, yyyy')}
                                   </p>
                              </div>
                              <div>
                                   <p className='text-darker/50 text-xs uppercase tracking-wider'>End</p>
                                   <p className='text-lg font-semibold'>
                                        {format(bookingData.endDate, 'MMM d, yyyy')}
                                   </p>
                              </div>
                              <div>
                                   <p className='text-darker/50 text-xs uppercase tracking-wider'>Duration</p>
                                   <p className='text-lg font-semibold'>
                                        {bookingData.days} {bookingData.days === 1 ? 'day' : 'days'}
                                   </p>
                              </div>
                         </div>
                    </div>

                    {/* Order Total */}
                    <div className='border-t border-gray-200 pt-4'>
                         <h4 className='text-yellow text-xs uppercase tracking-widest font-bold mb-3'>
                              Order Summary
                         </h4>
                         <div className='space-y-2 text-darker text-sm'>
                              <div className='flex justify-between'>
                                   <span className='text-darker/60'>
                                        Equipment rental ({bookingData.days} {bookingData.days === 1 ? 'day' : 'days'})
                                   </span>
                                   <span>${((bookingData.rentalCents || 0) / 100).toLocaleString()}</span>
                              </div>
                              {(bookingData.deliveryFeeCents || 0) > 0 && (
                                   <div className='flex justify-between'>
                                        <span className='text-darker/60'>Delivery fee</span>
                                        <span>${(bookingData.deliveryFeeCents / 100).toLocaleString()}</span>
                                   </div>
                              )}
                              {(bookingData.depositCents || 0) > 0 && (
                                   <div className='flex justify-between'>
                                        <span className='text-darker/60'>
                                             Refundable security deposit
                                        </span>
                                        <span>${(bookingData.depositCents / 100).toLocaleString()}</span>
                                   </div>
                              )}
                              <div className='flex justify-between'>
                                   <span className='text-darker/60'>
                                        Online convenience fee (3.2%)
                                   </span>
                                   <span>${((bookingData.convenienceFeeCents || 0) / 100).toFixed(2)}</span>
                              </div>
                              <div className='flex justify-between border-t border-gray-200 pt-3 mt-2 text-base'>
                                   <span className='font-bold text-darker'>Total</span>
                                   <span className='text-yellow font-bold text-xl'>
                                        ${(bookingData.totalCents / 100).toLocaleString()}
                                   </span>
                              </div>
                              {(bookingData.depositCents || 0) > 0 && (
                                   <p className='text-darker/50 text-xs mt-1'>
                                        The security deposit will be refunded upon return of equipment in good condition.
                                   </p>
                              )}
                         </div>
                    </div>

                    {/* Contact Info */}
                    <div className='border-t border-gray-200 pt-4'>
                         <h4 className='text-yellow text-xs uppercase tracking-widest font-bold mb-3'>
                              Contact Information
                         </h4>
                         <div className='text-darker space-y-1'>
                              <p className='font-semibold'>{contactData.name}</p>
                              <p className='text-darker/70'>{contactData.email}</p>
                              <p className='text-darker/70'>{contactData.phone}</p>
                              {contactData.notes && (
                                   <p className='text-darker/60 text-sm mt-2'>
                                        Notes: {contactData.notes}
                                   </p>
                              )}
                         </div>
                    </div>
               </div>

               {/* Rental Agreement */}
               <div className='mt-6 bg-white rounded-2xl border-2 border-gray-200 p-5 space-y-4'>
                    <h4 className='text-dark uppercase font-bold text-base tracking-wider'>
                         Safety &amp; Rental Agreement
                    </h4>

                    <Disclosure>
                         {({ open }) => (
                              <div className={`rounded-xl border duration-300 ${open ? 'border-yellow bg-yellow/5' : 'border-gray-200 bg-white'}`}>
                                   <Disclosure.Button className='flex w-full items-center justify-between px-4 py-3 text-left'>
                                        <span className='text-darker text-sm font-semibold'>
                                             Read the full rental agreement
                                        </span>
                                        <span className={`text-yellow text-xl flex-shrink-0 duration-300 ${open ? 'rotate-45' : ''}`}>
                                             +
                                        </span>
                                   </Disclosure.Button>
                                   <Transition
                                        enter='duration-200 ease-out'
                                        enterFrom='opacity-0 max-h-0'
                                        enterTo='opacity-100 max-h-[2000px]'
                                        leave='duration-150 ease-in'
                                        leaveFrom='opacity-100 max-h-[2000px]'
                                        leaveTo='opacity-0 max-h-0'
                                   >
                                        <Disclosure.Panel className='overflow-hidden'>
                                             <div className='px-4 pb-4 border-t border-yellow/30 pt-4 space-y-4 max-h-80 overflow-y-auto'>
                                                  {RENTAL_AGREEMENT_SECTIONS.map((section) => (
                                                       <div key={section.heading}>
                                                            <p className='text-darker font-bold text-sm mb-1'>
                                                                 {section.heading}
                                                            </p>
                                                            <p className='text-darker/80 text-xs leading-relaxed'>
                                                                 {section.body}
                                                            </p>
                                                       </div>
                                                  ))}
                                                  <p className='text-darker/40 text-xs pt-2 border-t border-gray-200'>
                                                       Agreement version {AGREEMENT_VERSION}
                                                  </p>
                                             </div>
                                        </Disclosure.Panel>
                                   </Transition>
                              </div>
                         )}
                    </Disclosure>

                    <label className='flex items-start gap-3 cursor-pointer'>
                         <input
                              type='checkbox'
                              checked={agreedToSafety}
                              onChange={(e) => {
                                   setAgreedToSafety(e.target.checked);
                                   if (e.target.checked) setShowValidation(false);
                              }}
                              className='mt-1 w-5 h-5 rounded border-gray-300 accent-yellow flex-shrink-0'
                         />
                         <span className='text-darker text-sm'>
                              I have reviewed the{' '}
                              <a
                                   href='/safety'
                                   target='_blank'
                                   rel='noreferrer'
                                   className='text-yellow font-semibold underline'
                              >
                                   equipment safety manuals
                              </a>{' '}
                              for each tool in my rental and understand how to operate them safely.
                         </span>
                    </label>

                    <label className='flex items-start gap-3 cursor-pointer'>
                         <input
                              type='checkbox'
                              checked={agreedToTerms}
                              onChange={(e) => {
                                   setAgreedToTerms(e.target.checked);
                                   if (e.target.checked) setShowValidation(false);
                              }}
                              className='mt-1 w-5 h-5 rounded border-gray-300 accent-yellow flex-shrink-0'
                         />
                         <span className='text-darker text-sm'>
                              I agree to the rental agreement above, including full replacement
                              cost for lost or damaged equipment and 100% liability for any
                              injury or harm during the rental.
                         </span>
                    </label>

                    {showValidation && !allAgreed && (
                         <p className='text-red-600 text-sm font-semibold'>
                              Please check both boxes to continue.
                         </p>
                    )}
               </div>

               <div className='mt-8 flex gap-4 justify-between'>
                    <button
                         type='button'
                         onClick={onBack}
                         disabled={submitting}
                         className='py-3 px-6 rounded-xl border-2 border-gray-200 text-darker/60 font-semibold hover:border-darker/30 hover:text-darker duration-300 disabled:opacity-50'
                    >
                         ← Back
                    </button>
                    <button
                         onClick={handleCheckout}
                         disabled={submitting || !allAgreed}
                         className='group relative font-semibold text-base uppercase tracking-wider bg-yellow text-dark pl-10 pr-6 py-4 rounded-xl hover:bg-yellow/90 duration-300 flex items-center disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
                    >
                         <span className='text-dark pr-2 absolute left-4 duration-300 group-hover:tracking-[0.15em]'>
                              {'//'}
                         </span>
                         {submitting ? 'Submitting...' : 'Submit Booking Request'}
                    </button>
               </div>
          </div>
     );
}
